import { useEffect, useRef } from "react";
import { subscribeScroll, prefersReducedMotion } from "@/lib/scrollEngine";

/**
 * Aurora light-strand field. A fixed full-viewport canvas (travels with the
 * viewer, so pinned sections never "freeze" it) with a bundle of flowing light
 * fibers plus soft aurora glow. Scroll speed bends and shifts the strands; time
 * keeps them gently alive. Screen-blended so it only adds light.
 */
const FiberStrands = () => {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const mobile = window.matchMedia("(max-width: 767px), (pointer: coarse)").matches;
    const isStatic =
      prefersReducedMotion() || !!(window as unknown as { __PRERENDER__?: boolean }).__PRERENDER__;
    const COUNT = mobile ? 10 : 22;
    const strands = Array.from({ length: COUNT }, (_, i) => ({
      off: (i / COUNT - 0.5) * 0.5,
      phase: Math.random() * Math.PI * 2,
      speed: 0.25 + Math.random() * 0.35,
      width: 0.6 + Math.random() * 1.4,
      alpha: 0.25 + Math.random() * 0.5,
      hue: Math.random() < 0.6 ? 186 : 190,
    }));

    let w = 0, h = 0;
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth; h = window.innerHeight;
      canvas.width = w * dpr; canvas.height = h * dpr;
      canvas.style.width = `${w}px`; canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    let first = true, scroll = 0, smooth = 0, vel = 0, t = 0;

    const render = () => {
      // ease scroll so strands glide rather than jump
      const prev = smooth;
      smooth += (scroll - smooth) * 0.08;
      vel += ((smooth - prev) - vel) * 0.1;
      const s = smooth / 900; // scroll phase
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = "lighter";

      // the bundle sweeps across the viewport as you go down the page
      const center = w * (0.5 + Math.sin(s * 0.9) * 0.3);
      const tilt = Math.cos(s * 0.7) * w * 0.35 + vel * 4;

      // aurora glow behind the fibers
      const gx = center + tilt * 0.2;
      const g = ctx.createRadialGradient(gx, h * 0.55, 0, gx, h * 0.55, Math.max(w, h) * 0.55);
      g.addColorStop(0, "hsla(186,100%,44%,0.16)");
      g.addColorStop(0.5, "hsla(190,79%,29%,0.06)");
      g.addColorStop(1, "hsla(190,79%,29%,0)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);

      for (const st of strands) {
        const steps = 28;
        const pts: [number, number][] = [];
        for (let k = 0; k <= steps; k++) {
          const y = (k / steps) * (h + 200) - 100;
          const v = y / h;
          const x =
            center +
            st.off * w +
            tilt * (v - 0.5) +
            Math.sin(v * 3 + st.phase + t * st.speed + s * 2) * w * 0.06 +
            Math.sin(v * 7 + st.phase * 2 - t * st.speed * 0.7) * w * 0.015;
          pts.push([x, y]);
        }
        const trace = () => {
          ctx.beginPath();
          ctx.moveTo(pts[0][0], pts[0][1]);
          for (let k = 1; k < pts.length - 1; k++) {
            const mx = (pts[k][0] + pts[k + 1][0]) / 2;
            const my = (pts[k][1] + pts[k + 1][1]) / 2;
            ctx.quadraticCurveTo(pts[k][0], pts[k][1], mx, my);
          }
        };
        const flicker = 0.75 + 0.25 * Math.sin(t * 1.3 + st.phase);
        // wide soft halo
        trace();
        ctx.strokeStyle = `hsla(${st.hue},100%,45%,${st.alpha * 0.06 * flicker})`;
        ctx.lineWidth = st.width * 40;
        ctx.stroke();
        // core
        trace();
        ctx.strokeStyle = `hsla(${st.hue},100%,62%,${st.alpha * 0.45 * flicker})`;
        ctx.lineWidth = st.width * 2.5;
        ctx.stroke();
      }
      ctx.globalCompositeOperation = "source-over";
    };

    let raf = 0, last = 0;
    const loop = (now: number) => {
      raf = requestAnimationFrame(loop);
      if (now - last < 1000 / 30) return;
      last = now;
      t += 1 / 30;
      render();
    };

    // Pinned (sticky scrollytelling) sections: while one is held in place the
    // aurora must hold too and only "breathe", so subtract the distance scrolled
    // inside every pinned section from the effective scroll position.
    const pinHosts = () =>
      Array.from(document.querySelectorAll<HTMLElement>("main .sticky"))
        .map((el) => el.parentElement)
        .filter((p): p is HTMLElement => !!p && p.offsetHeight > window.innerHeight * 1.2);
    let hosts = pinHosts();
    const hostTimer = window.setTimeout(() => (hosts = pinHosts()), 2000);
    const effectiveScroll = () => {
      let y = window.scrollY;
      const vh = window.innerHeight;
      for (const h of hosts) {
        const r = h.getBoundingClientRect();
        y -= Math.min(Math.max(-r.top, 0), Math.max(r.height - vh, 0));
      }
      return y;
    };

    const unsub = subscribeScroll(() => {
      scroll = effectiveScroll();
      if (first) { smooth = scroll; first = false; }
      if (isStatic || mobile) { smooth = scroll; render(); }
    });
    const onResize = () => { resize(); hosts = pinHosts(); render(); };
    window.addEventListener("resize", onResize);

    if (isStatic || mobile) render();
    else raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(hostTimer);
      unsub();
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-0"
      style={{ opacity: 0.8, filter: "blur(5px)" }}
    />
  );
};

export default FiberStrands;
